import { useConfig } from "../providers/config";
import { useCartData } from "../providers/cartDataStore";
import { useActiveOrMainShippingZone } from "../providers/shippingZones";
import { useGetShippingMethodsQuery } from "../providers/shippingAndPayment";
import { getLowerValueObj } from "../utils";
import { useState, useEffect } from "react";
import { ShippingMethod } from "@core/types";

interface UseGetMinimumOrderAmountResult {
  amountToRaise: number;
  minimumOrderAmount: number;
  isMinimumAmountReached: boolean;
}

const useGetMinimumOrderAmount = (): UseGetMinimumOrderAmountResult => {
  const config = useConfig();
  const { activeShippingZone } = useActiveOrMainShippingZone();
  const { id: activeShippingZoneId } = activeShippingZone;
  const { cartSubtotalValue } = useCartData();
  const [minimumOrderAmount, setMinimumOrderAmount] = useState(config.minimumOrderAmount);

  const { getProductsTotalWeight } = useCartData();

  const shouldEnableShippingQuery =
    config?.cartMinAmountFromMethods && (config.shipping.isShippingZonesEnabled || !!activeShippingZoneId);

  const { data: shippingMethods } = useGetShippingMethodsQuery(activeShippingZoneId, getProductsTotalWeight(), {
    enabled: shouldEnableShippingQuery,
  });

  useEffect(() => {
    const lowerValueObj = getLowerValueObj<ShippingMethod, "minAmount">(shippingMethods || [], "minAmount");

    const minimumOrderAmount = lowerValueObj?.minAmount ?? config.minimumOrderAmount;

    setMinimumOrderAmount(Number(minimumOrderAmount));
  }, [config.minimumOrderAmount, shippingMethods]);

  return {
    minimumOrderAmount,
    isMinimumAmountReached: cartSubtotalValue >= minimumOrderAmount,
    amountToRaise: Math.max(0, minimumOrderAmount - cartSubtotalValue),
  };
};

export default useGetMinimumOrderAmount;
